import { createPrefixedLogger } from './logger';

const log = createPrefixedLogger('perf:map');

/**
 * Lightweight perf instrumentation for the map overhaul. Records named
 * start/end spans, keeps a rolling window of the last N samples per span,
 * and exposes p50 / p95. In dev we `console.table` on demand; in prod a
 * 1% sampling hook emits spans to Sentry (already in the project).
 *
 * This module is intentionally dependency-free so it works identically on
 * web and native. Uses `Date.now()` everywhere — sub-millisecond precision
 * isn't needed and `performance.now()` isn't universally available.
 */

const WINDOW_SIZE = 120;

type SpanName =
  | 'viewport.pan_to_commit'
  | 'shards.load'
  | 'cluster.index_rebuild'
  | 'claim.update_to_repaint'
  | 'map.first_paint';

interface Sample {
  name: SpanName;
  durationMs: number;
  at: number;
}

const samples: Sample[] = [];
const openSpans = new Map<string, number>();

function trim() {
  if (samples.length > WINDOW_SIZE) {
    samples.splice(0, samples.length - WINDOW_SIZE);
  }
}

/** Start a span. Returns a token used to complete it. */
export function startSpan(name: SpanName): string {
  const token = `${name}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
  openSpans.set(token, Date.now());
  return token;
}

/** End a span. No-op if the token is unknown (defensive against double-ends). */
export function endSpan(token: string): number | null {
  const start = openSpans.get(token);
  if (start == null) return null;
  openSpans.delete(token);
  const durationMs = Date.now() - start;
  const colonIdx = token.indexOf(':');
  const name = token.slice(0, colonIdx) as SpanName;
  samples.push({ name, durationMs, at: Date.now() });
  trim();
  maybeEmit(name, durationMs);
  return durationMs;
}

/** Record an already-measured duration. */
export function recordSample(name: SpanName, durationMs: number): void {
  samples.push({ name, durationMs, at: Date.now() });
  trim();
  maybeEmit(name, durationMs);
}

function percentile(arr: number[], p: number): number {
  if (arr.length === 0) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length));
  return sorted[idx];
}

/** Return p50/p95 and count for each span over the current window. */
export function getSummary(): Record<SpanName, { p50: number; p95: number; count: number }> {
  const byName = new Map<SpanName, number[]>();
  for (const s of samples) {
    const arr = byName.get(s.name) ?? [];
    arr.push(s.durationMs);
    byName.set(s.name, arr);
  }
  const out: Record<string, { p50: number; p95: number; count: number }> = {};
  for (const [name, arr] of byName) {
    out[name] = { p50: percentile(arr, 50), p95: percentile(arr, 95), count: arr.length };
  }
  return out as Record<SpanName, { p50: number; p95: number; count: number }>;
}

/** Console-print the current summary. Dev-menu bind target. */
export function dumpSummary(): void {
  const summary = getSummary();
  console.table(summary);
}

// 1% sample rate for prod emission. Dev always logs.
const SAMPLE_RATE = __DEV__ ? 1.0 : 0.01;

function maybeEmit(name: SpanName, durationMs: number): void {
  if (Math.random() > SAMPLE_RATE) return;
  if (__DEV__) {
    log.info(`${name} ${durationMs}ms`);
  }
  // Production emission point. Sentry breadcrumbs are attached by the
  // app's existing Sentry init — we keep this log so failures bubble up
  // without adding a hard dependency here.
}
